/**
 * LetterPreview — Wave 3 (Credit-Repair Family) migration.
 *
 * Route: /credit/letters/:draftId
 *
 * Structured slots render read-only alongside editable AI-enriched
 * prose. Letter Quality Score anchors the page; below the threshold
 * the Finalize button is disabled and the failing checks show
 * remediation guidance.
 *
 * Rebuilt against the LandingV2 aesthetic bar (Wave 3 anchor bar
 * matches Wave 2 signature routes). Every previous data-testid,
 * fetch, patch, finalize, download URL, and Address Gap Gate flow is
 * preserved verbatim.
 */
import { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  ShieldAlert,
  UserX,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import { Badge, Button, Textarea } from "@/design-system";

const THRESHOLD = 85;

const CHECK_LABELS = {
  selected_accounts_present:     "All selected accounts present",
  dispute_reason_present:        "Dispute reason referenced in prose",
  facts_hallucination_passed:    "No invented facts",
  bureau_creditor_correct:       "Bureau + creditor names correct",
  account_numbers_masked:        "Account numbers masked",
  specific_remedy_present:       "Specific requested remedy present",
  attachments_checklist_present: "Attachments section present",
  relevant_citations_only:       "Only relevant FCRA citations",
  no_prohibited_guarantee:       "No prohibited guarantee language",
  length_ok:                     "Prose length within limits",
};

const STATUS_TONE = {
  finalized: "success",
  failed:    "danger",
};

export default function LetterPreview() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState("");
  const [detailed, setDetailed] = useState("");
  const [flagOff, setFlagOff] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const initialLoadRef = useRef(true);

  const reload = useCallback(async () => {
    if (initialLoadRef.current) setLoading(true);
    setFlagOff(false);
    try {
      const [res, ps] = await Promise.all([
        api.get(`/credit-engine/letters/${draftId}`),
        api.get(`/credit-engine/disputes/profile-status`).catch(() => ({ data: null })),
      ]);
      setDraft(res.data);
      const enriched = res.data?.edited_slots || res.data?.enriched_slots || {};
      setOpening(enriched.opening_paragraph || "");
      setDetailed(enriched.detailed_factual_dispute || "");
      setProfileStatus(ps?.data || null);
    } catch (e) {
      if (e?.response?.status === 503) setFlagOff(true);
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
    }
  }, [draftId]);

  useEffect(() => { reload(); }, [reload]);

  async function saveEdits() {
    setSaving(true);
    try {
      const res = await api.patch(`/credit-engine/letters/${draftId}`, {
        opening_paragraph: opening,
        detailed_factual_dispute: detailed,
      });
      setDraft(res.data);
      toast.success(`Saved. Quality score ${res.data?.quality_score}/100`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && Array.isArray(d?.reasons)) {
        toast.error(`Rejected: ${d.reasons.join("; ")}`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  async function finalize() {
    if (profileStatus && !profileStatus.complete) {
      toast.error("Complete your profile before finalizing this letter.");
      navigate("/profile");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/credit-engine/letters/${draftId}/finalize`);
      setDraft(res.data);
      toast.success("Letter finalized. Downloads are now available.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "consumer_profile_incomplete") {
        toast.error(d.message || "Complete your profile before finalizing.");
        setProfileStatus({
          complete: false,
          missing_fields: d.missing_fields || [],
          required_fields: d.required_fields || [],
          remediation_route: d.remediation_route || "/profile",
          message: d.message,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  function downloadUrl(fmt) {
    return `${API_BASE}/credit-engine/letters/${draftId}/download?format=${fmt}`;
  }

  if (flagOff) {
    return (
      <Shell>
        <div
          data-testid="letter-flag-off"
          className="rounded-[var(--ds-radius-lg)] p-10 md:p-14 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-[var(--ds-radius-md)]"
            style={{ background: "var(--ds-color-warning-soft)", color: "var(--ds-color-warning)" }}
          >
            <ShieldAlert className="w-6 h-6" aria-hidden />
          </span>
          <h1
            className="mt-5 text-[var(--ds-color-ink)]"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
            }}
          >
            Letter generator disabled.
          </h1>
          <p className="mt-3 max-w-lg mx-auto text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            The letter generator is currently disabled behind the{" "}
            <code className="text-[13px] font-mono">letter_generator_v2</code> flag.
          </p>
        </div>
      </Shell>
    );
  }

  if (loading || !draft) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Loading draft…
        </div>
      </Shell>
    );
  }

  const score = draft.quality_score || 0;
  const finalized = draft.status === "finalized";
  const failed = draft.status === "failed";
  const failingChecks = (draft.quality_checks || []).filter((c) => !c.passed);
  const structured = draft.structured_slots || {};
  const recipient = draft.recipient || {};
  const statusTone = STATUS_TONE[draft.status] || "accent";

  return (
    <Shell testId="letter-preview">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="letter-back-btn"
            className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            style={{ transitionDuration: "var(--ds-duration-fast)" }}
          >
            <ArrowLeft className="w-3.5 h-3.5" aria-hidden /> Back
          </button>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
            Credit Repair · Letter draft
          </div>
          <h1
            className="mt-2 text-[var(--ds-color-ink)] leading-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
            }}
          >
            Letter draft preview.
          </h1>
        </div>
        <Badge tone={statusTone} size="md" data-testid="letter-status-badge">
          {draft.status}
        </Badge>
      </div>

      {/* Address Gap Gate remediation banner */}
      {!finalized && profileStatus && !profileStatus.complete && (
        <section
          data-testid="letter-profile-remediation-banner"
          className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{
            background: "var(--ds-color-warning-soft)",
            border: "1px solid var(--ds-color-warning)",
          }}
        >
          <div className="flex items-start gap-3 min-w-0">
            <UserX
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              style={{ color: "var(--ds-color-warning)" }}
              aria-hidden
            />
            <div className="min-w-0">
              <div
                className="text-[var(--ds-color-ink)]"
                style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
              >
                Profile incomplete — cannot finalize yet.
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
                {profileStatus.message ||
                  "Add your legal name and mailing address to your profile before finalizing this letter."}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/profile")}
            data-testid="letter-profile-remediation-cta"
          >
            Complete profile
          </Button>
        </section>
      )}

      {/* Quality score */}
      <section
        className="rounded-[var(--ds-radius-lg)] p-5 md:p-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-border)",
          boxShadow: "var(--ds-shadow-card)",
        }}
      >
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Letter Quality Score
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <div
              data-testid="letter-quality-score"
              className="tabular-nums"
              style={{
                fontFamily: "var(--ds-font-display)",
                fontWeight: 600,
                fontSize: "2.5rem",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: score >= THRESHOLD ? "var(--ds-color-success)" : "var(--ds-color-warning)",
              }}
            >
              {score}
            </div>
            <div className="text-[13px] text-[var(--ds-color-ink-muted)]">
              / 100 (min {THRESHOLD} to finalize)
            </div>
          </div>
        </div>
        {!finalized && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              disabled={saving || failed}
              onClick={saveEdits}
              data-testid="letter-save-btn"
            >
              {saving ? "Saving…" : "Save edits & re-validate"}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                saving ||
                score < THRESHOLD ||
                failed ||
                (profileStatus && !profileStatus.complete)
              }
              onClick={finalize}
              data-testid="letter-finalize-btn"
              title={
                profileStatus && !profileStatus.complete
                  ? "Complete your profile before finalizing"
                  : undefined
              }
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> : <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />}
              Finalize
            </Button>
          </div>
        )}
        {finalized && (
          <div className="flex gap-2 flex-wrap">
            {["pdf", "docx", "txt"].map((fmt) => (
              <a
                key={fmt}
                href={downloadUrl(fmt)}
                data-testid={`letter-download-${fmt}-btn`}
                className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-3 py-1.5 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                style={{
                  background: "var(--ds-color-surface-muted)",
                  border: "1px solid var(--ds-color-border-strong)",
                  color: "var(--ds-color-ink)",
                  transitionDuration: "var(--ds-duration-fast)",
                }}
              >
                <Download className="w-3.5 h-3.5" aria-hidden /> {fmt.toUpperCase()}
              </a>
            ))}
          </div>
        )}
      </section>

      {/* Failing checks */}
      {failingChecks.length > 0 && (
        <section
          data-testid="letter-failing-checks"
          className="rounded-[var(--ds-radius-lg)] p-5"
          style={{
            background: "var(--ds-color-warning-soft)",
            border: "1px solid var(--ds-color-warning)",
          }}
        >
          <div
            className="text-[var(--ds-color-ink)] flex items-center gap-2"
            style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1rem" }}
          >
            <XCircle
              className="w-4 h-4"
              style={{ color: "var(--ds-color-warning)" }}
              aria-hidden
            />
            {failingChecks.length} check{failingChecks.length === 1 ? "" : "s"} to address before finalizing
          </div>
          <ul className="mt-3 text-[13px] leading-relaxed space-y-1.5 text-[var(--ds-color-ink)]">
            {failingChecks.map((c) => (
              <li key={c.key}>
                <strong>{CHECK_LABELS[c.key] || c.key}</strong>
                {c.remediation ? ` — ${c.remediation}` : ""}
                <span className="text-[var(--ds-color-ink-muted)]"> ({c.weight} pts)</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Structured slots — READ ONLY */}
        <section
          data-testid="letter-structured-slots"
          className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Structured facts (read-only)
          </div>
          <div className="mt-3 space-y-2 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            <SlotRow label="Recipient" value={`${recipient.label || ""} — ${recipient.org_name || ""}`} />
            <SlotRow label="Consumer" value={structured.consumer_name || "[Your Full Legal Name]"} />
            <SlotRow label="Mode" value={structured.mode} />
            <SlotRow label="Dispute reason" value={(structured.dispute_reason || "").replace(/_/g, " ")} />
            <SlotRow label="Tradelines bundled" value={structured.tradelines_count} />
          </div>
          <div className="mt-4 text-[12px] text-[var(--ds-color-ink-muted)]">
            To change any of these, go back and re-select the dispute.
          </div>
        </section>

        {/* AI-enriched slots — EDITABLE */}
        <section
          data-testid="letter-editable-slots"
          className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
            boxShadow: "var(--ds-shadow-card)",
          }}
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            AI-enriched prose (editable)
          </div>
          <label className="mt-3 block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Opening paragraph (40–300 chars)
          </label>
          <Textarea
            className="mt-1.5"
            rows={5}
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
            readOnly={finalized}
            maxLength={300}
            data-testid="letter-edit-opening-paragraph"
          />
          <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Detailed factual dispute (80–800 chars)
          </label>
          <Textarea
            className="mt-1.5"
            rows={8}
            value={detailed}
            onChange={(e) => setDetailed(e.target.value)}
            readOnly={finalized}
            maxLength={800}
            data-testid="letter-edit-detailed-dispute"
          />
        </section>
      </div>

      {/* Full rendered body */}
      <section
        data-testid="letter-rendered-body"
        className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-border)",
          boxShadow: "var(--ds-shadow-card)",
        }}
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)] flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" aria-hidden /> Rendered letter body
        </div>
        <pre
          className="mt-3 whitespace-pre-wrap font-mono text-[12px] md:text-[13px] overflow-x-auto text-[var(--ds-color-ink)]"
          style={{
            background: "var(--ds-color-surface-muted)",
            border: "1px solid var(--ds-color-border)",
            borderRadius: "var(--ds-radius-md)",
            padding: "14px 16px",
            lineHeight: 1.6,
          }}
        >
          {draft.rendered_body || "(not rendered)"}
        </pre>
      </section>
    </Shell>
  );
}

function SlotRow({ label, value }) {
  return (
    <div>
      <span className="text-[var(--ds-color-ink-muted)]">{label}:</span>{" "}
      <span className="text-[var(--ds-color-ink)]">{value}</span>
    </div>
  );
}

function Shell({ children, testId }) {
  return (
    <div
      data-testid={testId || "letter-root"}
      data-page-outer="wave-3"
      className="min-h-screen bg-[var(--ds-color-background)] text-[var(--ds-color-ink)]"
    >
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 md:px-8 pt-10 pb-20 space-y-6">{children}</main>
    </div>
  );
}
